<template>
  <div class="bento-card bg-noise" :class="{ 'featured': featured }" ref="cardRef" @mousemove="handleMouseMove">
    <div class="bento-content">
      <div class="bento-media">
         <img v-if="image" :src="image" :alt="title" class="bento-image" />
         <div class="bento-icon" v-else-if="icon">
            <!-- Custom SVG Shape Background -->
            <svg class="bento-icon-bg" viewBox="-60 -60 120 120" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M-46.189998626708984,-54.42599868774414 C-41.41699981689453,-57.630001068115234 -35.67499923706055,-59.5 -29.5,-59.5 C-29.5,-59.5 29.5,-59.5 29.5,-59.5 C46.05699920654297,-59.5 59.5,-46.05699920654297 59.5,-29.5 C59.5,-29.5 59.5,-29.5 59.5,-29.5 C59.5,-29.5 59.5,29.5 59.5,29.5 C59.5,46.05699920654297 46.05699920654297,59.5 29.5,59.5 C29.5,59.5 -29.5,59.5 -29.5,59.5 C-46.05699920654297,59.5 -59.5,46.05699920654297 -59.5,29.5 C-59.5,29.5 -59.5,-29.5 -59.5,-29.5 C-59.5,-39.88199996948242 -54.2140007019043,-49.040000915527344 -46.189998626708984,-54.42599868774414"
                stroke="rgb(240,240,240)" 
                stroke-opacity="0.32" 
                stroke-width="1"
                stroke-linecap="butt" 
                stroke-linejoin="miter" 
                stroke-miterlimit="4"
                fill="rgba(0, 255, 102, 0.1)"
              />
            </svg>
            <i :class="icon"></i>
         </div>
      </div>
      <h3>{{ title }}</h3>
      <p>{{ description }}</p>
      <slot></slot>
    </div>
    <div class="bento-glow"></div>
  </div>
</template>

<script setup>
import { defineProps, ref } from 'vue';

defineProps({
  title: String,
  description: String,
  icon: String,
  image: String,
  featured: {
    type: Boolean,
    default: false
  }
});

const cardRef = ref(null);

const handleMouseMove = (e) => {
  if (!cardRef.value) return;
  const rect = cardRef.value.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  
  cardRef.value.style.setProperty('--mouse-x', `${x}px`);
  cardRef.value.style.setProperty('--mouse-y', `${y}px`);
};
</script>

<style scoped>
.bento-card {
  position: relative;
  background: rgba(18, 18, 18, 0.4); /* More transparent */
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  padding: 30px;
  overflow: hidden;
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  height: 100%;
  display: flex;
  flex-direction: column;
  /* Spotlight Variables Defaults */
  --mouse-x: -100px;
  --mouse-y: -100px;
}

.bento-card:hover {
  transform: translateY(-5px);
  /* Border highlight via box-shadow fallback */
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.4),
    inset 0 1px 0px rgba(255, 255, 255, 0.1); /* Refractive Top Edge */ 
}

/* Spotlight Border Effect via pseudo-element */
.bento-card::before {
  content: "";
  position: absolute;
  inset: 0px;
  border-radius: 24px;
  padding: 1.5px; /* Border width */
  background: radial-gradient(
    600px circle at var(--mouse-x) var(--mouse-y),
    rgba(255, 255, 255, 0.6),
    transparent 40%
  );
  -webkit-mask: 
     linear-gradient(#fff 0 0) content-box, 
     linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0;
  transition: opacity 0.5s;
  pointer-events: none;
  z-index: 3;
}

.bento-card:hover::before {
  opacity: 1;
}

.bento-content {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.bento-media {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.bento-image {
  width: 100%;
  height: 160px;
  object-fit: cover;
  border-radius: 16px;
  margin-bottom: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.bento-icon {
  width: 50px;
  height: 50px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--neon-green);
  font-size: 1.5rem;
}

.bento-icon-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
}

.bento-card h3 {
  font-size: 1.5rem;
  margin-bottom: 15px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #fff;
}

.bento-card p {
  color: var(--secondary-text);
  font-size: 0.95rem;
  line-height: 1.6;
  flex-grow: 1;
}

/* Spotlight Surface Glow */
.bento-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    800px circle at var(--mouse-x) var(--mouse-y),
    rgba(255, 255, 255, 0.06),
    transparent 40%
  );
  opacity: 0;
  transition: opacity 0.5s;
  pointer-events: none;
  z-index: 1;
}

.bento-card:hover .bento-glow {
  opacity: 1;
}

/* Featured variation */
.bento-card.featured {
  background: linear-gradient(135deg, rgba(18, 18, 18, 0.8), rgba(0, 255, 102, 0.05));
  border-color: rgba(0, 255, 102, 0.3);
}

.bento-card.featured::before {
   background: radial-gradient(
    600px circle at var(--mouse-x) var(--mouse-y),
    rgba(0, 255, 102, 0.5),
    transparent 40%
  );
}

@media (max-width: 768px) {
  .bento-card:hover {
    transform: none; /* No lift on touch */
    box-shadow: none;
  }
  
  .bento-card::before,
  .bento-glow {
    display: none; /* Disable expensive spotlight on mobile */
  }
}
</style>
